import { useState, useRef, Fragment } from 'react'

const STL_CLEAN_STATUS = {
  pending:  { label: 'Pending',    cls: 'bg-gray-100 text-gray-600'  },
  cleaning: { label: 'Cleaning…',  cls: 'bg-blue-100 text-blue-700'  },
  done:     { label: 'Done',       cls: 'bg-green-100 text-green-700' },
  error:    { label: 'Error',      cls: 'bg-red-100 text-red-600'    },
}

function StlCleanTab() {
  const [queue,    setQueue]    = useState([])
  const [cleaning, setCleaning] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [config,   setConfig]   = useState({ minRatio: 0.05, minCells: 500, topN: 0 })
  const [expanded, setExpanded] = useState(new Set())
  const fileInputRef = useRef(null)

  function addFiles(files) {
    const stlFiles = Array.from(files).filter(f => f.name.toLowerCase().endsWith('.stl'))
    if (!stlFiles.length) return
    setQueue(prev => [
      ...prev,
      ...stlFiles.map(file => ({
        id: crypto.randomUUID(),
        file,
        status: 'pending',
        pct: 0,
        stage: null,
        stlBuffer: null,
        regions: null,
        totalIn: 0,
        totalOut: 0,
        elapsed: null,
        error: null,
      })),
    ])
  }

  function removeItem(id) {
    setQueue(prev => prev.filter(f => f.id !== id))
    setExpanded(prev => { const s = new Set(prev); s.delete(id); return s })
  }

  function clearAll() {
    setQueue([])
    setExpanded(new Set())
  }

  function toggleExpand(id) {
    setExpanded(prev => {
      const s = new Set(prev)
      s.has(id) ? s.delete(id) : s.add(id)
      return s
    })
  }

  async function cleanFile(item) {
    const cfg = { ...config } // snapshot at call time
    const update = patch => setQueue(prev => prev.map(f => f.id === item.id ? { ...f, ...patch } : f))
    update({ status: 'cleaning', pct: 0, stage: 'Reading file…' })

    try {
      const buf = await item.file.arrayBuffer()

      await new Promise((resolve, reject) => {
        const worker = new Worker(
          new URL('../../workers/stl-clean-worker.js', import.meta.url),
          { type: 'module' },
        )

        worker.onmessage = (e) => {
          const msg = e.data
          if (msg.type === 'progress') {
            update({ pct: msg.pct, stage: msg.stage })
            return
          }
          if (msg.type === 'done') {
            update({
              status: 'done', pct: 100, stage: null,
              stlBuffer: msg.stlBuffer, regions: msg.regions,
              totalIn: msg.totalIn, totalOut: msg.totalOut, elapsed: msg.elapsed,
            })
            worker.terminate()
            resolve()
            return
          }
          if (msg.type === 'error') {
            worker.terminate()
            reject(new Error(msg.message))
          }
        }
        worker.onerror = (err) => { worker.terminate(); reject(err) }

        worker.postMessage({ buffer: buf, config: cfg }, [buf])
      })
    } catch (err) {
      setQueue(prev => prev.map(f => f.id === item.id
        ? { ...f, status: 'error', stage: null, error: err.message || 'Worker error' }
        : f,
      ))
    }
  }

  async function cleanAll() {
    setCleaning(true)
    for (const item of queue) {
      if (item.status !== 'pending') continue
      await cleanFile(item)
    }
    setCleaning(false)
  }

  function downloadFile(item) {
    if (!item.stlBuffer) return
    const blob = new Blob([item.stlBuffer], { type: 'application/octet-stream' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = item.file.name.replace(/\.stl$/i, '_CLEAN.stl')
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  async function downloadAllZip() {
    const done = queue.filter(f => f.status === 'done' && f.stlBuffer)
    if (!done.length) return
    const { zipSync } = await import('fflate')
    const files = {}
    for (const item of done) {
      files[item.file.name.replace(/\.stl$/i, '_CLEAN.stl')] = new Uint8Array(item.stlBuffer)
    }
    const zip = zipSync(files)
    const blob = new Blob([zip], { type: 'application/zip' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = 'stl_clean_batch.zip'
    document.body.appendChild(a); a.click()
    document.body.removeChild(a); URL.revokeObjectURL(url)
  }

  const pendingCount = queue.filter(f => f.status === 'pending').length
  const doneCount    = queue.filter(f => f.status === 'done').length

  return (
    <div className="space-y-6">

      {/* Drop zone */}
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files) }}
        onClick={() => fileInputRef.current?.click()}
        className={`card p-10 text-center cursor-pointer transition-colors border-2 border-dashed ${
          dragOver ? 'border-brand-500 bg-brand-50' : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <svg className="w-10 h-10 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
        </svg>
        <p className="text-sm text-gray-600 font-medium">Drop STL files here</p>
        <p className="text-xs text-gray-400 mt-1">or click to browse — multiple files supported</p>
        <input
          ref={fileInputRef}
          type="file" accept=".stl" multiple className="hidden"
          onChange={e => { addFiles(e.target.files); e.target.value = '' }}
        />
      </div>

      {/* Config */}
      <div className="card p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Cleaning Settings</h3>
        <div className="flex flex-wrap gap-8">

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-500">Min Region Size</span>
            <div className="flex items-center gap-2">
              <input
                type="range" min="1" max="20" step="1"
                value={Math.round(config.minRatio * 100)}
                onChange={e => setConfig(c => ({ ...c, minRatio: parseInt(e.target.value) / 100 }))}
                className="w-32 accent-brand-600"
              />
              <span className="text-sm font-mono w-10 text-gray-700">{Math.round(config.minRatio * 100)}%</span>
            </div>
            <span className="text-xs text-gray-400">Keep regions ≥ X% of largest. Mandible ≈ 30–60%.</span>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-500">Min Face Floor</span>
            <input
              type="number" min="0" max="10000" step="100"
              value={config.minCells}
              onChange={e => setConfig(c => ({ ...c, minCells: parseInt(e.target.value) || 0 }))}
              className="w-24 text-sm border border-gray-300 rounded px-2 py-1"
            />
            <span className="text-xs text-gray-400">Always drop regions below this face count.</span>
          </label>

          <label className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-500">Force Keep Top N</span>
              <span className="text-xs font-semibold text-brand-cyan w-12 text-right">
                {config.topN === 0 ? 'Off' : `Top ${config.topN}`}
              </span>
            </div>
            <input
              type="range"
              min={0}
              max={25}
              step={1}
              value={config.topN}
              onChange={e => setConfig(c => ({ ...c, topN: parseInt(e.target.value) }))}
              className="w-full accent-brand-cyan"
            />
            <div className="flex justify-between text-[10px] text-gray-300 -mt-1">
              <span>Off</span>
              <span>5</span>
              <span>10</span>
              <span>15</span>
              <span>20</span>
              <span>25</span>
            </div>
            <span className="text-xs text-gray-400">Always keep the N largest regions.</span>
          </label>

        </div>
      </div>

      {/* Toolbar */}
      {queue.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            <button
              onClick={cleanAll}
              disabled={cleaning || pendingCount === 0}
              className="btn-primary text-sm px-4 py-2"
            >
              {cleaning ? 'Cleaning…' : `Clean All (${pendingCount})`}
            </button>
            {doneCount > 1 && (
              <button onClick={downloadAllZip} className="btn-secondary text-sm px-4 py-2">
                Download All ({doneCount}) as ZIP
              </button>
            )}
          </div>
          <button onClick={clearAll} className="text-xs text-gray-400 hover:text-red-500 transition-colors">
            Clear all
          </button>
        </div>
      )}

      {/* File queue */}
      {queue.length > 0 && (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100 text-xs text-gray-500">
                <th className="text-left px-4 py-2 font-medium">File</th>
                <th className="text-right px-3 py-2 font-medium">Raw Faces</th>
                <th className="text-center px-3 py-2 font-medium">Regions (kept/total)</th>
                <th className="text-right px-3 py-2 font-medium">Clean Faces</th>
                <th className="text-center px-3 py-2 font-medium">Status</th>
                <th className="text-center px-4 py-2 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {queue.map(item => {
                const st = STL_CLEAN_STATUS[item.status]
                const isExpanded = expanded.has(item.id)
                const keptCount  = item.regions?.filter(r => r.kept).length ?? null
                const totalCount = item.regions?.length ?? null

                return (
                  <Fragment key={item.id}>
                    <tr className="border-b border-gray-50 hover:bg-gray-50 align-middle">

                      {/* Filename — click to expand region breakdown */}
                      <td className="px-4 py-2.5 max-w-xs">
                        <button
                          onClick={() => item.regions && toggleExpand(item.id)}
                          className={`text-left font-medium text-gray-800 truncate block w-full ${item.regions ? 'hover:text-brand-600 cursor-pointer' : 'cursor-default'}`}
                          title={item.file.name}
                        >
                          {item.file.name}
                          {item.regions && (
                            <span className="ml-1 text-gray-400 text-xs">{isExpanded ? '▲' : '▼'}</span>
                          )}
                        </button>
                        {item.status === 'cleaning' && (
                          <div className="h-1 bg-gray-200 rounded-full mt-1.5 overflow-hidden w-44">
                            <div
                              className="h-full bg-brand-500 rounded-full transition-all duration-300"
                              style={{ width: `${item.pct}%` }}
                            />
                          </div>
                        )}
                        {item.elapsed && <span className="text-xs text-gray-400 block mt-0.5">{item.elapsed}s</span>}
                        {item.error  && <span className="text-xs text-red-500 block mt-0.5 truncate">{item.error}</span>}
                      </td>

                      <td className="px-3 py-2.5 text-right font-mono text-xs text-gray-600">
                        {item.totalIn ? item.totalIn.toLocaleString() : '—'}
                      </td>

                      <td className="px-3 py-2.5 text-center text-xs text-gray-600">
                        {keptCount !== null ? `${keptCount} / ${totalCount}` : '—'}
                      </td>

                      <td className="px-3 py-2.5 text-right font-mono text-xs text-gray-600">
                        {item.totalOut ? item.totalOut.toLocaleString() : '—'}
                      </td>

                      <td className="px-3 py-2.5 text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>
                          {item.status === 'cleaning' ? (item.stage || 'Cleaning…') : st.label}
                        </span>
                      </td>

                      <td className="px-4 py-2.5 text-center">
                        <div className="flex items-center justify-center gap-3">
                          {item.status === 'done' && (
                            <button onClick={() => downloadFile(item)} className="text-xs text-green-600 hover:text-green-800 font-medium">
                              Download
                            </button>
                          )}
                          {item.status === 'pending' && !cleaning && (
                            <button onClick={() => cleanFile(item)} className="text-xs text-brand-600 hover:text-brand-800 font-medium">
                              Clean
                            </button>
                          )}
                          <button onClick={() => removeItem(item.id)} className="text-xs text-gray-400 hover:text-red-500">✕</button>
                        </div>
                      </td>
                    </tr>

                    {/* Region breakdown — toggled by clicking filename */}
                    {isExpanded && item.regions && (
                      <tr>
                        <td colSpan={6} className="px-6 py-3 bg-gray-50 border-b border-gray-100">
                          <table className="text-xs border border-gray-200 rounded overflow-hidden">
                            <thead>
                              <tr className="bg-gray-100 text-gray-500">
                                <th className="px-3 py-1 text-right font-medium">Rank</th>
                                <th className="px-3 py-1 text-right font-medium">Faces</th>
                                <th className="px-3 py-1 text-right font-medium">% of largest</th>
                                <th className="px-3 py-1 text-center font-medium">Decision</th>
                              </tr>
                            </thead>
                            <tbody>
                              {item.regions.map(r => (
                                <tr key={r.rank} className={r.kept ? 'bg-green-50' : 'bg-red-50'}>
                                  <td className="px-3 py-1 text-right text-gray-500">{r.rank}</td>
                                  <td className="px-3 py-1 text-right font-mono">{r.size.toLocaleString()}</td>
                                  <td className="px-3 py-1 text-right font-mono">
                                    {item.regions[0].size > 0
                                      ? ((r.size / item.regions[0].size) * 100).toFixed(1) + '%'
                                      : '—'}
                                  </td>
                                  <td className="px-3 py-1 text-center font-semibold">
                                    {r.kept
                                      ? <span className="text-green-700">KEEP</span>
                                      : <span className="text-red-500">DROP</span>}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {queue.length === 0 && (
        <p className="text-center text-sm text-gray-400 py-4">
          Drop STL files above to remove scan noise while preserving disconnected anatomies (skull + mandible, etc.).<br />
          All processing happens in your browser — no files are uploaded to any server.
        </p>
      )}

    </div>
  )
}

export default StlCleanTab
